export function getTailwindColor(numbers: number) {
	const colors = ['#a7f3d0', '#F5F3FF', '#fed7aa', '#fecaca', '#ffff']; // Corresponding to 'emerald-200', 'violet-200', 'amber-200', 'red-200'
	return colors[numbers];
}

export function generateTailwindColor(str: string) {
	const colors = ['#DC2626', '#16A34A', '#7C3AED', '#CA8A04']; // Corresponding to 'emerald-200', 'violet-200', 'amber-200', 'red-200'

	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}

	const index = Math.abs(hash) % colors.length;
	return colors[index];
}

/**
 * Hex ↔ HSL-triple conversion.
 *
 * The app's theme is authored as bare HSL triples ("250.96 100% 54.9%") because
 * Tailwind composes them with an alpha channel (`hsl(var(--primary) / 0.5)`).
 * Native `<input type="color">` only speaks hex, so the White Labeling form
 * converts at the boundary rather than shipping a colour-picker dependency.
 */

/** "#7C3AED" → "250.9 100% 54.5%". Returns '' for anything unparseable. */
export function hexToHslTriple(hex: string): string {
	const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
	if (!match) return '';

	const int = parseInt(match[1] ?? '', 16);
	const r = ((int >> 16) & 255) / 255;
	const g = ((int >> 8) & 255) / 255;
	const b = (int & 255) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;
	const l = (max + min) / 2;

	let h = 0;
	if (delta !== 0) {
		if (max === r) h = ((g - b) / delta) % 6;
		else if (max === g) h = (b - r) / delta + 2;
		else h = (r - g) / delta + 4;
		h *= 60;
		if (h < 0) h += 360;
	}

	const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

	const round = (n: number) => Math.round(n * 10) / 10;
	return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

/** "250.9 100% 54.5%" → "#7C3AED". Returns `fallback` for anything unparseable. */
export function hslTripleToHex(triple: string, fallback = '#7C3AED'): string {
	const match = /^(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)%\s+(\d{1,3}(?:\.\d+)?)%$/.exec(
		triple.trim(),
	);
	if (!match) return fallback;

	const h = Number(match[1]);
	const s = Number(match[2]) / 100;
	const l = Number(match[3]) / 100;

	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;

	const [r, g, b] = (
		h < 60
			? [c, x, 0]
			: h < 120
				? [x, c, 0]
				: h < 180
					? [0, c, x]
					: h < 240
						? [0, x, c]
						: h < 300
							? [x, 0, c]
							: [c, 0, x]
	).map((v) => Math.round((v + m) * 255)) as [number, number, number];

	return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Hex ↔ HSV.
 *
 * Storage is HSL, but a saturation/value square is HSV - white in one corner,
 * black along the bottom - and converting HSL straight to that geometry gives a
 * square whose top edge is not white, so half of it is unreachable. The picker
 * therefore works in HSV and converts once, at the edge, before handing a hex to
 * `hexToHslTriple`. Cheaper than a colour-picker dependency for two conversions.
 */

/** "#7C3AED" → `{ h: 258.3, s: 0.76, v: 0.93 }`. Null for anything unparseable. */
export function hexToHsv(hex: string): { h: number; s: number; v: number } | null {
	const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
	if (!match) return null;

	const int = parseInt(match[1] ?? '', 16);
	const r = ((int >> 16) & 255) / 255;
	const g = ((int >> 8) & 255) / 255;
	const b = (int & 255) / 255;

	const max = Math.max(r, g, b);
	const delta = max - Math.min(r, g, b);

	let h = 0;
	if (delta !== 0) {
		if (max === r) h = ((g - b) / delta) % 6;
		else if (max === g) h = (b - r) / delta + 2;
		else h = (r - g) / delta + 4;
		h *= 60;
		if (h < 0) h += 360;
	}

	return { h, s: max === 0 ? 0 : delta / max, v: max };
}

/** `(258.3, 0.76, 0.93)` → "#7c3aed". `s`/`v` are 0-1, `h` is wrapped into 0-360. */
export function hsvToHex(hue: number, s: number, v: number): string {
	const h = ((hue % 360) + 360) % 360;
	const c = v * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = v - c;

	const [r, g, b] = (
		h < 60
			? [c, x, 0]
			: h < 120
				? [x, c, 0]
				: h < 180
					? [0, c, x]
					: h < 240
						? [0, x, c]
						: h < 300
							? [x, 0, c]
							: [c, 0, x]
	).map((channel) => Math.round((channel + m) * 255)) as [number, number, number];

	return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The brand ramp, derived from a single white-label colour.
 *
 * `applyDocumentBrand` used to set only `--primary` and `--ring`, but the app is
 * painted from the `--brand*` family (~800 usages), so an organization's colour
 * barely showed. This rebuilds the whole family from the org's hue.
 *
 * Only hue and chroma move. Each token keeps the lightness it was authored with,
 * because lightness is what carries the contrast ratios the palette was checked
 * against - a colour picker must not be able to make the nav text unreadable.
 * Saturation is scaled by the ratio the org's colour bears to the default brand,
 * so a muted brand reads muted across the ramp instead of snapping back to full.
 */
type Ramp = Record<string, [saturation: number, lightness: number]>;

/** Saturation/lightness per token, transcribed from `index.css`. */
const BRAND_RAMP: Record<'light' | 'dark', { baseSaturation: number; tokens: Ramp }> = {
	light: {
		baseSaturation: 83,
		tokens: {
			'--brand': [83, 58],
			'--brand-hover': [90, 66],
			'--brand-light': [92, 76],
			'--brand-deep': [69, 42],
			'--brand-subtle': [100, 97],
			'--brand-subtle-foreground': [69, 42],
			'--brand-border': [72, 89],
			// `text-brand` / `text-primary`. In light the brand is already text-safe.
			'--brand-text': [83, 58],
			'--primary': [83, 58],
			'--primary-hover': [90, 66],
			'--ring': [83, 58],
			'--field-focus': [55, 52],
		},
	},
	dark: {
		baseSaturation: 96,
		tokens: {
			'--brand': [96, 71],
			'--brand-hover': [96, 78],
			'--brand-light': [92, 84],
			// Text only, so in dark it takes the light step, as index.css does.
			'--brand-deep': [92, 84],
			'--brand-subtle': [38, 15],
			'--brand-subtle-foreground': [96, 86],
			'--brand-border': [40, 30],
			// `text-brand` / `text-primary`: the fill-weight --brand fails as text in dark.
			'--brand-text': [92, 84],
			'--primary': [92, 66],
			'--primary-hover': [96, 72],
			'--ring': [92, 66],
			'--field-focus': [70, 66],
		},
	},
};

/** The three gradient stops, per theme, as `[saturation, lightness]` pairs. */
/** Three [saturation, lightness] stops: start, middle (55%), end. */
type GradientStops = [[number, number], [number, number], [number, number]];

const GRADIENT_STOPS: Record<'light' | 'dark', { brand: GradientStops; hover: GradientStops }> = {
	light: {
		brand: [[83, 58], [90, 66], [92, 76]],
		hover: [[69, 42], [83, 58], [90, 66]],
	},
	dark: {
		brand: [[80, 56], [92, 66], [94, 74]],
		hover: [[84, 62], [94, 72], [96, 80]],
	},
};

const HSL_TRIPLE =
	/^(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)%\s+(\d{1,3}(?:\.\d+)?)%$/;

const clamp = (n: number) => Math.min(100, Math.max(0, Math.round(n * 10) / 10));

/**
 * CSS overriding the brand family for both themes, or '' when the triple is
 * unparseable - an unreadable stored value falls back to the shipped palette
 * rather than painting the app with `NaN`.
 */
export function brandThemeVars(
	triple: string,
	theme: 'light' | 'dark',
): Record<string, string> | null {
	const match = HSL_TRIPLE.exec(triple.trim());
	if (!match) return null;

	const hue = Number(match[1]);
	const scale = (s: number) => clamp((s * Number(match[2])) / BRAND_RAMP[theme].baseSaturation);

	const vars: Record<string, string> = {};
	for (const [name, [s, l]] of Object.entries(BRAND_RAMP[theme].tokens)) {
		vars[name] = `${hue} ${scale(s)}% ${l}%`;
	}

	const gradient = (stops: GradientStops) =>
		`linear-gradient(135deg, hsl(${hue} ${scale(stops[0][0])}% ${stops[0][1]}%), hsl(${hue} ${scale(stops[1][0])}% ${stops[1][1]}%) 55%, hsl(${hue} ${scale(stops[2][0])}% ${stops[2][1]}%))`;
	vars['--gradient-brand'] = gradient(GRADIENT_STOPS[theme].brand);
	vars['--gradient-brand-hover'] = gradient(GRADIENT_STOPS[theme].hover);

	return vars;
}

/**
 * The brand ramp as a stylesheet for the whole document.
 *
 * The White Labeling preview needs the same ramp confined to one subtree instead,
 * which is what `brandThemeVars` is for - both read the one `BRAND_RAMP`, so the
 * preview cannot drift from what saving actually paints.
 */
export function brandThemeCss(triple: string): string {
	const block = (theme: 'light' | 'dark') => {
		const vars = brandThemeVars(triple, theme);
		return vars
			? Object.entries(vars)
					.map(([name, value]) => `${name}:${value};`)
					.join('')
			: null;
	};

	const light = block('light');
	const dark = block('dark');
	if (!light || !dark) return '';

	// Same specificity as the authored rules but later in the cascade, so the
	// dark block still wins inside `.dark` exactly as it does in index.css.
	return `:root{${light}}\n.dark,[data-theme='dark']{${dark}}`;
}
